// Analytics and Performance Monitoring Service
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { trace } from 'firebase/performance';
import { analytics, performance } from '../config/firebase';

// Custom event types for the FMK app
export const ANALYTICS_EVENTS = {
  // Authentication
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  LOGOUT: 'logout',
  
  // User Actions
  PROFILE_VIEW: 'profile_view',
  PROFILE_EDIT: 'profile_edit',
  PHOTO_UPLOAD: 'photo_upload',
  
  // Game Actions
  GAME_START: 'game_start',
  GAME_COMPLETE: 'game_complete',
  CARD_SWIPE: 'card_swipe',
  MATCH_MADE: 'match_made',
  
  // Messaging
  MESSAGE_SENT: 'message_sent',
  CONVERSATION_STARTED: 'conversation_started',
  
  // Location & Dating Features
  LOCATION_ENABLED: 'location_enabled',
  CRUISE_MODE_ACTIVATED: 'cruise_mode_activated',
  
  // Premium Features
  PREMIUM_VIEWED: 'premium_viewed',
  PREMIUM_PURCHASED: 'premium_purchased',
  
  // Settings & Preferences
  SETTINGS_CHANGED: 'settings_changed',
  PRIVACY_SETTINGS_CHANGED: 'privacy_settings_changed',
  
  // Errors & Issues
  ERROR_OCCURRED: 'error_occurred',
  BUG_REPORTED: 'bug_reported',
  
  // Performance
  PAGE_VIEW: 'page_view',
  SCREEN_VIEW: 'screen_view'
} as const;

// Analytics Service Class
class AnalyticsService {
  private isEnabled: boolean = false;

  constructor() {
    // Enable analytics in production or when explicitly enabled
    this.isEnabled = !import.meta.env.DEV || import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
    
    if (!analytics && this.isEnabled) {
      console.warn('📊 Analytics not initialized but service is enabled');
    }
  }

  // Set user identification for analytics
  setUser(userId: string, properties?: Record<string, any>) {
    if (!this.isEnabled || !analytics) return;
    
    try {
      setUserId(analytics, userId);
      
      if (properties) {
        setUserProperties(analytics, properties);
      }
      
      console.log('📊 User set for analytics:', userId);
    } catch (error) {
      console.error('📊 Error setting user for analytics:', error);
    }
  }

  // Log custom events
  logEvent(eventName: string, parameters?: Record<string, any>) {
    if (!this.isEnabled || !analytics) return;
    
    try {
      logEvent(analytics, eventName, {
        timestamp: Date.now(),
        ...parameters
      });
      
      console.log('📊 Event logged:', eventName, parameters);
    } catch (error) {
      console.error('📊 Error logging event:', error);
    }
  }

  // Convenience methods for common events
  logPageView(pageName: string, additionalParams?: Record<string, any>) {
    this.logEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
      page_name: pageName,
      ...additionalParams
    });
  }

  logScreenView(screenName: string, screenClass?: string) {
    this.logEvent(ANALYTICS_EVENTS.SCREEN_VIEW, {
      screen_name: screenName,
      screen_class: screenClass || screenName
    });
  }

  logUserAction(action: string, details?: Record<string, any>) {
    this.logEvent(action, {
      category: 'user_action',
      ...details
    });
  }

  logError(error: string, context?: Record<string, any>) {
    this.logEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      error_message: error,
      error_context: context?.context || 'unknown',
      ...context
    });
  }

  // Performance monitoring
  createTrace(traceName: string) {
    if (!performance) {
      console.warn('⚡ Performance monitoring not available');
      return null;
    }

    try {
      const performanceTrace = trace(performance, traceName);
      console.log('⚡ Performance trace created:', traceName);
      return performanceTrace;
    } catch (error) {
      console.error('⚡ Error creating performance trace:', error);
      return null;
    }
  }

  // Measure and log performance
  async measurePerformance<T>(
    traceName: string, 
    operation: () => Promise<T>,
    metadata?: Record<string, string>
  ): Promise<T> {
    const performanceTrace = this.createTrace(traceName);
    
    if (performanceTrace) {
      performanceTrace.start();
      if (metadata) {
        Object.entries(metadata).forEach(([key, value]) => {
          performanceTrace.putAttribute(key, value);
        });
      }
    }

    try {
      const result = await operation();
      
      if (performanceTrace) {
        performanceTrace.stop();
      }
      
      return result;
    } catch (error) {
      if (performanceTrace) {
        performanceTrace.stop();
      }
      throw error;
    }
  }

  // Log timing manually
  logTiming(name: string, startTime: number, endTime: number = Date.now()) {
    const duration = endTime - startTime;
    this.logEvent('timing_complete', {
      name,
      value: duration
    });
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Convenience exports
export const {
  setUser,
  logEvent: logAnalyticsEvent,
  logPageView,
  logScreenView,
  logUserAction,
  logError,
  createTrace,
  measurePerformance,
  logTiming
} = analyticsService;

export default analyticsService;